import React from 'react';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import type { NavLink } from '@atlassian/jira-navigation-types/src/types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createContainer,
	createHook,
	createStore,
} from '@atlassian/react-sweet-state';
import { getItemFromLink } from '../transformer/index.tsx';
import type { ShortcutItem } from '../types.tsx';
import type { ContainerProps, State } from './types.tsx';

const renderShortcutIcon = () => <LinkExternalIcon spacing="spacious" label="shortcut" />;

/**
 * Actions
 * =======
 */

/**
 * Add item
 */
export const addItem =
	(item: ShortcutItem): Action<State> =>
	({ setState, getState }) => {
		const { shortcutType } = item;
		const state = getState();
		const index = state.data.map((i) => i.shortcutType).lastIndexOf(shortcutType);
		const newItem = {
			before: renderShortcutIcon,
			...item,
			target: '_blank',
			canEdit: true,
			itemType: 'shortcut',
		};

		// Append new item to same shortcuts group
		if (index === -1) {
			// @ts-expect-error - TS2322 - Type 'ShortcutItem | { target: string; canEdit: boolean; itemType: string; before: ComponentType<Record<any, any>> | (() => Element); href?: string | undefined; ... 7 more ...; text?: string | undefined; }' is not assignable to type 'ShortcutItem'. | TS2322 - Type '{ target: string; canEdit: boolean; itemType: string; before: React.ComponentType<Record<any, any>> | (() => JSX.Element); href?: string | undefined; icon?: string | undefined; ... 6 more ...; text?: string | undefined; }' is not assignable to type 'ShortcutItem'.
			setState({ data: [...state.data, newItem] });
		} else {
			setState({
				// @ts-expect-error - TS2322 - Type '(ShortcutItem | { target: string; canEdit: boolean; itemType: string; before: ComponentType<Record<any, any>> | (() => Element); href?: string | undefined; ... 7 more ...; text?: string | undefined; })[]' is not assignable to type 'ShortcutItem[]'.
				data: [...state.data.slice(0, index + 1), newItem, ...state.data.slice(index + 1)],
			});
		}
		state.onRefresh?.();
	};

/**
 * Delete item
 */
export const deleteItem =
	(id: string): Action<State> =>
	({ setState, getState }) => {
		const { data, onRefresh } = getState();
		const newItems = data.filter((existingItem) => existingItem.id !== id);

		setState({ data: newItems });
		onRefresh?.();
	};

/**
 * Edit item
 */
export const editItem =
	(item: ShortcutItem): Action<State> =>
	({ setState, getState }) => {
		const { data, onRefresh } = getState();
		const newItems = data.map((existingItem) =>
			existingItem.id !== item.id ? existingItem : item,
		);

		setState({ data: newItems });
		onRefresh?.();
	};

/**
 * Edit items
 */
export const editItems =
	(items: ShortcutItem[]): Action<State> =>
	({ setState, getState }) => {
		if (!items.length) {
			return;
		}

		const { data, onRefresh } = getState();
		const newItems = data.map(
			(existingItem) => items.find((item) => item.id === existingItem.id) || existingItem,
		);

		setState({ data: newItems });
		onRefresh?.();
	};

/**
 * Update container
 */
export const updateContainer =
	(data: NavLink[], loading: boolean, onRefresh?: () => void): Action<State> =>
	({ setState, getState }) => {
		const items: ShortcutItem[] = (data || []).map(getItemFromLink);
		const state = getState();

		return setState({
			...state,
			loading,
			data: items,
			oldData: items,
			isLoaded: true,
			onRefresh,
		});
	};

export const actions = {
	addItem,
	deleteItem,
	editItem,
	editItems,
	updateContainer,
} as const;

export type Actions = typeof actions;

/**
 * Store
 * =======
 */

/**
 * Initial state
 */
export const initialState: State = {
	data: [],
	oldData: null,
	loading: false,
	isLoaded: false,
};

/**
 * Instance
 */
const Store = createStore<State, Actions>({
	name: 'shortcuts',
	initialState,
	actions,
});

export default Store;

export const ShortcutsContainer = createContainer<State, Actions, ContainerProps>(Store, {
	onInit:
		() =>
		({ dispatch }, { data, loading, onRefresh }: ContainerProps) => {
			dispatch(actions.updateContainer(data, loading, onRefresh));
		},
	onUpdate:
		() =>
		({ dispatch }, { data, loading, onRefresh }: ContainerProps) => {
			dispatch(actions.updateContainer(data, loading, onRefresh));
		},
});

export const useShortcutItems = createHook(Store);
